import { Icon } from './Icons';
import React, { useState, useEffect, useRef } from 'react';
import { Radar } from 'react-chartjs-2';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip } from 'chart.js';
import { getCompanyData, calcAverage, calcBMIFromData } from '../company';
import { loadLatestMetrics } from '../db';
import { saveCompanyCode } from '../company';
import InterventionBooking from './InterventionBooking';
import TeamChallenge from './TeamChallenge';
import DataExport from './DataExport';
import { apiPost } from '../api';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip);

// ── Sub-components ────────────────────────────────────────────────────────────

function StatTile({ label, value, unit, color, sub }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
      {color && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: color, borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }} />}
      <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-head)', fontSize: '2rem', fontWeight: 400, color: color || 'var(--text)', lineHeight: 1 }}>{value || '—'}</div>
      {unit && <div style={{ fontSize: '.7rem', color: 'var(--text3)', marginTop: '.35rem', fontWeight: 300 }}>{unit}</div>}
      {sub && <div style={{ fontSize: '.68rem', color: color || 'var(--text3)', marginTop: '.3rem', fontWeight: 500, letterSpacing: '.04em' }}>{sub}</div>}
    </div>
  );
}

function RiskCard({ label, count, total, color, icon }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const barW = Math.min(100, pct);
  return (
    <div style={{ flex: 1, background: 'var(--bg-card)', border: `1px solid var(--border2)`, borderRadius: 'var(--radius-lg)', padding: '1.5rem', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '3px', background: color, borderRadius: 'var(--radius-lg) 0 0 var(--radius-lg)' }} />
      <div style={{ paddingLeft: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.85rem' }}>
          <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{label}</div>
          <Icon name={icon} size={14} color={color} />
        </div>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '2.8rem', fontWeight: 400, color: 'var(--text)', lineHeight: 1, marginBottom: '.6rem' }}>{count}</div>
        <div style={{ height: '3px', background: 'var(--bg-elevated)', borderRadius: '2px', overflow: 'hidden', marginBottom: '.5rem' }}>
          <div style={{ width: barW + '%', height: '100%', background: color, borderRadius: '2px', transition: 'width .8s ease' }} />
        </div>
        <div style={{ fontSize: '.7rem', color: 'var(--text3)', fontWeight: 300 }}>{pct}% of workforce</div>
      </div>
    </div>
  );
}

function AlertBanner({ icon, title, text, color, action, onAction }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', padding: '1rem 1.25rem', background: 'var(--bg-card)', border: `1px solid var(--border2)`, borderLeft: `3px solid ${color}`, borderRadius: 'var(--radius-lg)', marginBottom: '.6rem' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '.1rem' }}>
        <Icon name={icon} size={13} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '.82rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>{title}</div>
        <div style={{ fontSize: '.73rem', color: 'var(--text3)', lineHeight: 1.65, fontWeight: 300 }}>{text}</div>
      </div>
      {action && <button onClick={onAction} style={{ flexShrink: 0, padding: '.35rem .85rem', borderRadius: 'var(--radius)', background: 'transparent', border: `1px solid ${color}`, color, fontFamily: 'var(--font-body)', fontSize: '.7rem', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{action}</button>}
    </div>
  );
}

function CoverageBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round(count / total * 100) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 56px', alignItems: 'center', gap: '1rem', padding: '.45rem 0', borderBottom: '1px solid var(--border2)' }}>
      <div style={{ fontSize: '.75rem', color: 'var(--text2)', fontWeight: 400 }}>{label}</div>
      <div style={{ height: '4px', background: 'var(--bg-elevated)', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color || 'var(--primary)', borderRadius: '2px', transition: 'width .8s ease' }} />
      </div>
      <div style={{ fontSize: '.7rem', color: 'var(--text3)', textAlign: 'right' }}>{pct}%</div>
    </div>
  );
}

// ── Rico HR Chat ───────────────────────────────────────────────────────────────

function RicoHR({ companyData, companyCode }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const MSG_LIMIT = 30;
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const { blood = [], mental = [], body = [], fitness = [], totalEmployees = 0 } = companyData || {};

  const avg = (arr, key) => {
    const vals = arr.map(d => parseFloat(d?.[key])).filter(v => !isNaN(v) && v > 0);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };

  const teamSummary = {
    totalEmployees,
    bloodDataCoverage: blood.length + '/' + totalEmployees,
    mentalDataCoverage: mental.length + '/' + totalEmployees,
    avgGlucose: avg(blood, 'fastingGlucose'),
    avgSystolicBP: avg(blood, 'systolicBP'),
    avgCholesterol: avg(blood, 'totalCholesterol'),
    avgVitaminD: avg(blood, 'vitaminD'),
    avgCRP: avg(blood, 'crp'),
    avgStress: avg(mental, 'stressScore'),
    avgBurnout: avg(mental, 'burnoutScore'),
    avgSleep: avg(mental, 'sleepHours'),
    avgSteps: avg(fitness, 'stepsPerDay'),
    avgBMI: avg(body.map(b => ({ bmi: b.height && b.weight ? (parseFloat(b.weight) / Math.pow(parseFloat(b.height) / 100, 2)).toFixed(1) : null })), 'bmi'),
    highRiskCount: blood.filter(b => parseFloat(b.fastingGlucose) >= 7 || parseFloat(b.systolicBP) >= 140 || parseFloat(b.totalCholesterol) >= 6).length,
    moderateRiskCount: blood.filter(b => (parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7) || (parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140)).length,
    elevatedStressCount: mental.filter(m => parseFloat(m.stressScore) >= 65).length,
    burnoutRiskCount: mental.filter(m => parseFloat(m.burnoutScore) >= 60).length,
    companyCode,
  };

  const SUGGESTIONS = [
    "What does my team's health data tell you?",
    "Which health issues should I prioritise?",
    "How does our stress compare to national averages?",
    "What interventions would have the biggest impact?",
    "How can I improve our data coverage?",
  ];

  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: `Hi — I'm Rico, your team health advisor. I have your team's aggregated health data loaded for ${companyCode}.\n\nI can see ${totalEmployees} employees on the platform with ${blood.length} blood results and ${mental.length} mental health records. What would you like to know about your team's health?`
    }]);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  async function sendMessage(text) {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setLoading(true);
    setMsgCount(c => c + 1);
    try {
      const data = await apiPost('/api/hr-chat', {
        messages: newMessages.slice(-20).map(m => ({ role: m.role, content: m.content })),
        teamData: teamSummary
      });
      setMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection issue. Please try again.' }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px - 8rem)', maxHeight: 780, maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', marginBottom: '1.25rem', flexShrink: 0 }}>
        <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-head)', fontSize: '.85rem', fontWeight: 500, color: 'var(--primary)' }}>R</div>
        <div>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: '1.2rem', color: 'var(--text)' }}>Rico — Team Health Advisor</div>
          <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>Real Integrated Clinical Observations · {totalEmployees} employees · team data loaded</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'var(--success-dim)', border: '1px solid rgba(0,200,150,.2)', fontSize: '.65rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.06em' }}>✓ Team data loaded</div>
          <div style={{ padding: '.3rem .75rem', borderRadius: '20px', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', fontSize: '.65rem', color: msgCount >= MSG_LIMIT ? 'var(--danger)' : 'var(--text3)' }}>
            {MSG_LIMIT - msgCount} messages left
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '.85rem', marginBottom: '1rem' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: '.6rem', alignItems: 'flex-end' }}>
            {msg.role === 'assistant' && <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', fontWeight: 600, color: 'var(--primary)', flexShrink: 0 }}>R</div>}
            <div style={{ maxWidth: '78%', background: msg.role === 'user' ? 'var(--primary-dim)' : 'var(--bg-card)', border: msg.role === 'user' ? '1px solid rgba(0,174,239,0.2)' : '1px solid var(--border2)', borderRadius: msg.role === 'user' ? 'var(--radius-lg) 4px var(--radius-lg) var(--radius-lg)' : '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.1rem', fontSize: '.83rem', lineHeight: 1.7, color: 'var(--text)', fontWeight: 300, whiteSpace: 'pre-wrap' }}>{msg.content}</div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.6rem', color: 'var(--primary)', flexShrink: 0 }}>R</div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '4px var(--radius-lg) var(--radius-lg) var(--radius-lg)', padding: '.9rem 1.2rem', display: 'flex', gap: '.3rem' }}>
              {[0,1,2].map(j => <div key={j} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)', opacity: .5, animation: `bounce 1.2s ${j*.2}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length <= 1 && (
        <div style={{ flexShrink: 0, marginBottom: '.85rem', display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => sendMessage(s)} style={{ padding: '.4rem .85rem', borderRadius: '20px', background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text2)', fontSize: '.73rem', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 300, transition: 'all .15s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text2)'; }}>
              {s}
            </button>
          ))}
        </div>
      )}

      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
          <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder="Ask Rico about your team's health…" rows={1}
            style={{ flex: 1, resize: 'none', padding: '.8rem 1rem', borderRadius: 'var(--radius-lg)', lineHeight: 1.5, minHeight: '46px', maxHeight: '120px', fontFamily: 'var(--font-body)', fontSize: '.85rem', background: 'var(--bg-card)', border: '1px solid var(--border2)', color: 'var(--text)', outline: 'none' }} />
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading} style={{ padding: '.8rem 1.4rem', borderRadius: 'var(--radius-lg)', border: 'none', background: input.trim() && !loading ? 'var(--primary)' : 'var(--bg-elevated)', color: input.trim() && !loading ? '#ffffff' : 'var(--text3)', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: input.trim() && !loading ? 'pointer' : 'default', height: '46px', flexShrink: 0, transition: 'all .15s' }}>
            Send →
          </button>
        </div>
        <div style={{ marginTop: '.4rem', fontSize: '.65rem', color: 'var(--text3)', textAlign: 'center' }}>Rico uses anonymised, aggregated team data only. No individual employee data is identifiable.</div>
      </div>
      <style>{'@keyframes bounce { 0%,60%,100%{transform:translateY(0);opacity:.5} 30%{transform:translateY(-4px);opacity:1} }'}</style>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function HRDashboard({ activeTab: navTab = 0 }) {
  const [tab, setTab] = useState(0);
  const [companyCode, setCompanyCode] = useState('');
  const [inputCode, setInputCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function loadProfile() {
      setLoading(true);
      try {
        const profile = await loadLatestMetrics('profile');
        if (profile?.companyCode) {
          const code = profile.companyCode.toUpperCase().trim();
          setCompanyCode(code);
          await loadData(code);
        } else {
          setLoading(false);
          setError('No company linked to your account. Please contact Levare Wellbeing.');
        }
      } catch (e) { setError('Error loading company data.'); setLoading(false); }
    }
    loadProfile();
  }, []);

  async function loadData(code) {
    setLoading(true); setError('');
    try {
      const data = await getCompanyData(code);
      if (!data) setError('No company found with that code, or no members have joined yet.');
      else setCompanyData(data);
    } catch (e) { setError('Error loading company data.'); }
    setLoading(false);
  }

  if (loading) return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ width: '200px', height: '28px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }} />
      <div style={{ width: '300px', height: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }} />
    </div>
  );

  if (!companyCode || error) return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: '420px', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', color: 'var(--text)', marginBottom: '.5rem' }}>Team Dashboard</h1>
        <p style={{ fontSize: '.82rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>{error || 'Loading your team data…'}</p>
      </div>
    </div>
  );

  const { blood = [], body = [], mental = [], fitness = [], totalEmployees = 0 } = companyData || {};

  const avg = (arr, key) => {
    const vals = arr.map(d => parseFloat(d?.[key])).filter(v => !isNaN(v) && v > 0);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  };

  const avgStress      = avg(mental, 'stressScore');
  const avgBurnout     = avg(mental, 'burnoutScore');
  const avgGlucose     = avg(blood, 'fastingGlucose');
  const avgCholesterol = avg(blood, 'totalCholesterol');
  const avgSystolic    = avg(blood, 'systolicBP');
  const avgVitD        = avg(blood, 'vitaminD');
  const avgCRP         = avg(blood, 'crp');
  const avgSleep       = avg(mental, 'sleepHours');
  const avgSteps       = avg(fitness, 'stepsPerDay');
  const avgBMI         = avg(body.map(b => ({ bmi: calcBMIFromData(b) })), 'bmi');

  const highRisk    = blood.filter(b => parseFloat(b.fastingGlucose) >= 7 || parseFloat(b.systolicBP) >= 140 || parseFloat(b.totalCholesterol) >= 6).length;
  const moderateRisk= blood.filter(b => (parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7) || (parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140)).length;
  const lowRisk     = Math.max(0, totalEmployees - highRisk - moderateRisk);
  const highStress  = mental.filter(m => parseFloat(m.stressScore) >= 65).length;
  const burnoutRisk = mental.filter(m => parseFloat(m.burnoutScore) >= 60).length;
  const lowVitD     = blood.filter(b => b.vitaminD && parseFloat(b.vitaminD) < 50).length;

  const engagement = totalEmployees > 0 ? Math.round((blood.length + mental.length) / (totalEmployees * 2) * 100) : 0;

  const TABS = [
    { id: 0, label: 'Overview' },
    { id: 1, label: 'Mental Health' },
    { id: 2, label: 'Physical Health' },
    { id: 3, label: 'Risk Report' },
    { id: 4, label: 'Ask Rico' },
    { id: 5, label: 'Book Intervention' },
    { id: 6, label: 'Team Challenge' },
  ];

  // Radar scoring: 100 = optimal, 0 = worst case, all clamped 0-100
  function radarScore(value, optimal, worst) {
    if (!value) return 50;
    const v = parseFloat(value);
    // Linear interpolation: optimal = 100, worst = 0
    const score = 100 * (1 - Math.abs(v - optimal) / Math.abs(worst - optimal));
    return Math.min(100, Math.max(0, Math.round(score)));
  }

  const radarData = {
    labels: ['Glucose', 'Cholesterol', 'Blood Pressure', 'Mental Health', 'Fitness', 'BMI'],
    datasets: [{ data: [
      radarScore(avgGlucose, 4.5, 9.0),
      radarScore(avgCholesterol, 4.0, 7.5),
      radarScore(avgSystolic, 110, 170),
      radarScore(avgStress, 20, 100),
      avgSteps ? Math.min(100, Math.max(0, Math.round((parseFloat(avgSteps) - 2000) / (10000 - 2000) * 100))) : 50,
      radarScore(avgBMI, 22, 35),
    ], borderColor: 'var(--primary)', backgroundColor: 'rgba(0,174,239,.06)', pointBackgroundColor: 'var(--primary)', borderWidth: 2, pointRadius: 4 }]
  };

  const radarOpts = {
    responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
    scales: { r: { min: 0, max: 100, grid: { color: 'rgba(128,128,128,.15)' }, angleLines: { color: 'rgba(128,128,128,.15)' }, ticks: { display: false }, pointLabels: { color: '#888', font: { size: 11, family: 'DM Sans' } } } }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>Team Health Dashboard</h1>
          <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>{companyCode} · {totalEmployees} employee{totalEmployees !== 1 ? 's' : ''} · {engagement}% data coverage</p>
        </div>
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
          {highRisk > 0 && <div style={{ padding: '.4rem .9rem', borderRadius: 'var(--radius)', background: 'var(--danger-dim)', border: '1px solid rgba(240,80,80,.2)', fontSize: '.72rem', fontWeight: 600, color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '.4rem' }}><Icon name="x-circle" size={12} /> {highRisk} high risk</div>}
          {highStress > 0 && <div style={{ padding: '.4rem .9rem', borderRadius: 'var(--radius)', background: 'var(--warning-dim)', border: '1px solid rgba(240,160,80,.2)', fontSize: '.72rem', fontWeight: 600, color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '.4rem' }}><Icon name="activity" size={12} /> {highStress} high stress</div>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border2)', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '.1rem' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '.6rem 1.1rem', fontSize: '.82rem', color: tab === t.id ? 'var(--primary)' : 'var(--text3)', cursor: 'pointer', border: 'none', background: 'none', borderBottom: tab === t.id ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', fontFamily: 'var(--font-body)', fontWeight: tab === t.id ? 500 : 400, whiteSpace: 'nowrap', transition: 'color .15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 0 && (
        <div>
          {/* Hero risk strip */}
          <div style={{ display: 'flex', gap: '.85rem', marginBottom: '1.25rem' }}>
            <RiskCard label="High Risk" count={highRisk} total={totalEmployees} color="var(--danger)" icon="x-circle" />
            <RiskCard label="Moderate Risk" count={moderateRisk} total={totalEmployees} color="var(--warning)" icon="warning" />
            <RiskCard label="Low Risk" count={lowRisk} total={totalEmployees} color="var(--success)" icon="check-circle" />
          </div>

          {/* Key metrics grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '.85rem', marginBottom: '1.25rem' }}>
            <StatTile label="Team Size" value={totalEmployees} unit="employees" color="var(--primary)" />
            <StatTile label="Data Coverage" value={engagement + '%'} unit="have uploaded data" color={engagement >= 70 ? 'var(--success)' : engagement >= 40 ? 'var(--warning)' : 'var(--danger)'} />
            <StatTile label="Avg Stress" value={avgStress} unit="/100" color={avgStress ? (parseFloat(avgStress) < 40 ? 'var(--success)' : parseFloat(avgStress) < 65 ? 'var(--warning)' : 'var(--danger)') : null} sub={avgStress ? (parseFloat(avgStress) < 40 ? 'Low risk' : parseFloat(avgStress) < 65 ? 'Moderate' : 'High risk') : null} />
            <StatTile label="Avg Glucose" value={avgGlucose} unit="mmol/L" color={avgGlucose ? (parseFloat(avgGlucose) < 5.5 ? 'var(--success)' : parseFloat(avgGlucose) < 7 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg BP" value={avgSystolic} unit="mmHg systolic" color={avgSystolic ? (parseFloat(avgSystolic) < 120 ? 'var(--success)' : parseFloat(avgSystolic) < 140 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Cholesterol" value={avgCholesterol} unit="mmol/L" color={avgCholesterol ? (parseFloat(avgCholesterol) < 5 ? 'var(--success)' : parseFloat(avgCholesterol) < 6 ? 'var(--warning)' : 'var(--danger)') : null} />
          </div>

          {/* Radar + alerts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '.85rem', marginBottom: '1.25rem' }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Health Radar</div>
              {blood.length > 0 ? <div style={{ height: '220px' }}><Radar data={radarData} options={radarOpts} /></div> : <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: '.8rem' }}>Not enough data yet</div>}
              <div style={{ marginTop: '1rem', padding: '.65rem .85rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', fontSize: '.68rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
                Each axis scores 0–100. <span style={{ color: 'var(--success)' }}>100 = optimal</span>, <span style={{ color: 'var(--danger)' }}>0 = high risk</span>. A larger shape means a healthier team overall.
              </div>
            </div>

            <div>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.85rem' }}>Action Required</div>
              {highRisk > 0 && <AlertBanner icon="alert" title={highRisk + ' employee' + (highRisk > 1 ? 's' : '') + ' in high-risk category'} text={'Blood glucose ≥7 mmol/L, systolic BP ≥140 mmHg or cholesterol ≥6 mmol/L. These individuals are at elevated risk of type 2 diabetes or cardiovascular events. Consider a targeted wellbeing intervention.'} color="var(--danger)" action="Book intervention" onAction={() => setTab(5)} />}
              {highStress > 0 && <AlertBanner icon="flame" title={highStress + ' employee' + (highStress > 1 ? 's' : '') + ' with high stress (≥65/100)'} text={'Chronic high stress drives cortisol elevation, directly worsening glucose, cholesterol and blood pressure. A Levare stress management workshop would directly address this.'} color="var(--warning)" action="Book workshop" onAction={() => setTab(5)} />}
              {burnoutRisk > 0 && <AlertBanner icon="zap" title={burnoutRisk + ' at risk of burnout (score ≥60/100)'} text={"Burnout significantly increases absenteeism and long-term sick leave costs. Early intervention through Levare's resilience programme is the most cost-effective approach."} color="var(--warning)" action="View programmes" onAction={() => setTab(5)} />}
              {lowVitD > 0 && <AlertBanner icon="sun" title={lowVitD + ' employee' + (lowVitD > 1 ? 's' : '') + ' with low Vitamin D (<50 nmol/L)'} text={'Vitamin D deficiency impairs immune function, mood and energy. Particularly common in UK workforces. Supplementation education or a group order is a low-cost, high-impact intervention.'} color="var(--primary)" />}
              {totalEmployees > 0 && blood.length < Math.round(totalEmployees * 0.5) && <AlertBanner icon="bar-chart" title={'Only ' + blood.length + ' of ' + totalEmployees + ' employees have uploaded health data'} text={'Low data coverage limits your ability to identify health risks proactively. Share the employee app link and encourage uploads — it takes under 5 minutes.'} color="var(--primary)" />}
              {highRisk === 0 && highStress === 0 && burnoutRisk === 0 && blood.length >= Math.round(totalEmployees * 0.5) && (
                <AlertBanner icon="check-circle" title="No urgent flags — team health looks good" text={'Based on current data, your team does not have any high-priority health concerns. Continue monitoring and consider preventative programmes to maintain this.'} color="var(--success)" action="Ask Rico" onAction={() => setTab(4)} />
              )}
            </div>
          </div>

          {/* Coverage */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Data Coverage</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              <div>
                <CoverageBar label="Blood results" count={blood.length} total={totalEmployees} color="var(--primary)" />
                <CoverageBar label="Mental health" count={mental.length} total={totalEmployees} color="var(--success)" />
              </div>
              <div>
                <CoverageBar label="Body metrics" count={body.length} total={totalEmployees} color="var(--warning)" />
                <CoverageBar label="Fitness data" count={fitness.length} total={totalEmployees} color="var(--accent)" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MENTAL HEALTH ── */}
      {tab === 1 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '.85rem', marginBottom: '1rem' }}>
            <StatTile label="Avg Stress" value={avgStress} unit="/100" color={avgStress ? (parseFloat(avgStress) < 40 ? 'var(--success)' : parseFloat(avgStress) < 65 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Burnout" value={avgBurnout} unit="/100" color={avgBurnout ? (parseFloat(avgBurnout) < 40 ? 'var(--success)' : parseFloat(avgBurnout) < 60 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Sleep" value={avgSleep} unit="hrs/night" color={avgSleep ? (parseFloat(avgSleep) >= 7 ? 'var(--success)' : parseFloat(avgSleep) >= 6 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="High Stress" value={highStress} unit={'of ' + totalEmployees} color={highStress > 0 ? 'var(--danger)' : 'var(--success)'} />
            <StatTile label="Burnout Risk" value={burnoutRisk} unit={'of ' + totalEmployees} color={burnoutRisk > 0 ? 'var(--warning)' : 'var(--success)'} />
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Mental Health Coverage</div>
            <CoverageBar label="Stress score" count={mental.filter(m => m.stressScore).length} total={totalEmployees} color="var(--warning)" />
            <CoverageBar label="Burnout score" count={mental.filter(m => m.burnoutScore).length} total={totalEmployees} color="var(--danger)" />
            <CoverageBar label="Sleep data" count={mental.filter(m => m.sleepHours).length} total={totalEmployees} color="var(--primary)" />
          </div>
        </div>
      )}

      {/* ── PHYSICAL HEALTH ── */}
      {tab === 2 && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '.85rem', marginBottom: '1rem' }}>
            <StatTile label="Avg BP" value={avgSystolic} unit="mmHg" color={avgSystolic ? (parseFloat(avgSystolic) < 120 ? 'var(--success)' : parseFloat(avgSystolic) < 140 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Glucose" value={avgGlucose} unit="mmol/L" color={avgGlucose ? (parseFloat(avgGlucose) < 5.5 ? 'var(--success)' : parseFloat(avgGlucose) < 7 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Cholesterol" value={avgCholesterol} unit="mmol/L" color={avgCholesterol ? (parseFloat(avgCholesterol) < 5 ? 'var(--success)' : parseFloat(avgCholesterol) < 6 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg BMI" value={avgBMI} unit="kg/m²" color={avgBMI ? (parseFloat(avgBMI) < 25 ? 'var(--success)' : parseFloat(avgBMI) < 30 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Vitamin D" value={avgVitD} unit="nmol/L" color={avgVitD ? (parseFloat(avgVitD) >= 50 ? 'var(--success)' : parseFloat(avgVitD) >= 30 ? 'var(--warning)' : 'var(--danger)') : null} />
            <StatTile label="Avg Steps" value={avgSteps ? Math.round(parseFloat(avgSteps)).toLocaleString() : null} unit="per day" color={avgSteps ? (parseFloat(avgSteps) >= 8000 ? 'var(--success)' : 'var(--warning)') : null} />
          </div>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
            <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Blood Results Coverage</div>
            {['fastingGlucose','totalCholesterol','systolicBP','vitaminD','crp','tsh','ferritin'].map(key => (
              <CoverageBar key={key} label={key.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase())} count={blood.filter(b=>b[key]).length} total={totalEmployees} color="var(--primary)" />
            ))}
          </div>
        </div>
      )}

      {/* ── RISK REPORT ── */}
      {tab === 3 && (
        <div>
          <div style={{ padding: '.75rem 1rem', background: 'var(--primary-dim)', border: '1px solid rgba(0,174,239,0.2)', borderRadius: 'var(--radius)', marginBottom: '1.25rem', fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300 }}>
            🔒 Individual identity is protected. Only anonymised, aggregated scores are shown. Full individual data is accessible only to the Levare clinical team.
          </div>
          <div style={{ display: 'flex', gap: '.85rem', marginBottom: '1.25rem' }}>
            <RiskCard label="High Risk" count={highRisk} total={totalEmployees} color="var(--danger)" icon="x-circle" />
            <RiskCard label="Moderate Risk" count={moderateRisk} total={totalEmployees} color="var(--warning)" icon="warning" />
            <RiskCard label="Low Risk" count={lowRisk} total={totalEmployees} color="var(--success)" icon="check-circle" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '.85rem' }}>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border2)' }}>
                <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Clinical Flags</div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border2)' }}>
                    <th style={{ padding: '.6rem 1.5rem', fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'left' }}>Condition</th>
                    <th style={{ padding: '.6rem 1.5rem', fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', width: '80px' }}>Count</th>
                    <th style={{ padding: '.6rem 1.5rem', fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right', width: '80px' }}>% Team</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Glucose ≥7 mmol/L', blood.filter(b => parseFloat(b.fastingGlucose) >= 7).length, 'var(--danger)'],
                    ['Glucose 5.6–6.9 mmol/L', blood.filter(b => parseFloat(b.fastingGlucose) >= 5.6 && parseFloat(b.fastingGlucose) < 7).length, 'var(--warning)'],
                    ['Systolic BP ≥140 mmHg', blood.filter(b => parseFloat(b.systolicBP) >= 140).length, 'var(--danger)'],
                    ['Systolic BP 120–139 mmHg', blood.filter(b => parseFloat(b.systolicBP) >= 120 && parseFloat(b.systolicBP) < 140).length, 'var(--warning)'],
                    ['Total cholesterol ≥6 mmol/L', blood.filter(b => parseFloat(b.totalCholesterol) >= 6).length, 'var(--danger)'],
                    ['Vitamin D <50 nmol/L', blood.filter(b => b.vitaminD && parseFloat(b.vitaminD) < 50).length, 'var(--warning)'],
                    ['Stress score ≥65', mental.filter(m => parseFloat(m.stressScore) >= 65).length, 'var(--danger)'],
                    ['Burnout score ≥60', mental.filter(m => parseFloat(m.burnoutScore) >= 60).length, 'var(--warning)'],
                  ].map(([label, count, color], i, arr) => (
                    <tr key={label} style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border2)' : 'none' }}>
                      <td style={{ padding: '.75rem 1.5rem', fontSize: '.78rem', color: 'var(--text2)', fontWeight: 300 }}>{label}</td>
                      <td style={{ padding: '.75rem 1.5rem', textAlign: 'right' }}>
                        <span style={{ fontSize: '.88rem', fontWeight: 600, color: count > 0 ? color : 'var(--success)', fontFamily: 'var(--font-head)' }}>{count}</span>
                      </td>
                      <td style={{ padding: '.75rem 1.5rem', textAlign: 'right', fontSize: '.72rem', color: 'var(--text3)' }}>
                        {totalEmployees > 0 ? Math.round(count / totalEmployees * 100) + '%' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '1rem' }}>Data Completion</div>
              <CoverageBar label="Blood results" count={blood.length} total={totalEmployees} color="var(--primary)" />
              <CoverageBar label="Mental health" count={mental.length} total={totalEmployees} color="var(--success)" />
              <CoverageBar label="Body metrics" count={body.length} total={totalEmployees} color="var(--warning)" />
              <CoverageBar label="Fitness data" count={fitness.length} total={totalEmployees} color="var(--accent)" />
            </div>
          </div>
        </div>
      )}

      {tab === 4 && <RicoHR companyData={companyData} companyCode={companyCode} />}
      {tab === 5 && <InterventionBooking companyData={companyData} />}
      {tab === 6 && <TeamChallenge companyCode={companyCode} companyData={companyData} />}

      {tab < 4 && companyData && (
        <div style={{ marginTop: '1.5rem' }}>
          <DataExport mode="hr" companyData={companyData} />
        </div>
      )}
    </div>
  );
}
